import { Button, CloseButton, Group, Modal, Select, TextInput, Text, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCheck, IconRefresh, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  approveOrgRequest,
  assignScopeToMultipleOrgOrProduct,
  getScopes,
} from "../../utils/api-communication/RestApiCli";
import { useTranslation } from "react-i18next";
import { Scope } from "types/managementTypes";
import { generatePassword as generateValidPassword, isValidPassword } from "@utils/passwordUtils";

const NO_SCOPE_OPTION_VALUE = "__NO_SCOPE__";

interface ScopeOption {
  value: string;
  label: string;
  scopeItems?: string[];
}

const NO_SCOPE_OPTION: ScopeOption = { value: NO_SCOPE_OPTION_VALUE, label: "No scope" };

export default function OrganizationAccountRequestModal(props) {
  const { t } = useTranslation();
  const [scopeOptions, setScopeOptions] = useState<ScopeOption[]>([NO_SCOPE_OPTION]);
  const [selectedScopeName, setSelectedScopeName] = useState<string | null>(null);
  const [isLoadingScopes, setIsLoadingScopes] = useState(false);

  const generatePassword = generateValidPassword;

  useEffect(() => {
    if (props.org) {
      form.setFieldValue("orgId", props.org?.orgId ?? "");
    }
  }, [props.org]);

  useEffect(() => {
    if (!props.opened) {
      return;
    }

    setSelectedScopeName(null);
    setIsLoadingScopes(true);

    getScopes(
      (response) => {
        const uniqueScopes = new Map<string, ScopeOption>();

        (response.data as Scope[] | undefined)?.forEach((scope) => {
          if (!scope?.name || uniqueScopes.has(scope.name)) {
            return;
          }

          uniqueScopes.set(scope.name, {
            value: scope.name,
            label: scope.name,
            scopeItems: scope.list?.map((item) => String(item)) || [],
          });
        });

        const sortedScopes = Array.from(uniqueScopes.values()).sort((left, right) =>
          left.label.localeCompare(right.label),
        );

        setScopeOptions([NO_SCOPE_OPTION, ...sortedScopes]);
        setIsLoadingScopes(false);
      },
      (error) => {
        console.error("Failed to fetch scopes", error);
        setScopeOptions([NO_SCOPE_OPTION]);
        setIsLoadingScopes(false);
      },
    );
  }, [props.opened]);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      orgId: "",
      password: generatePassword(),
    },

    validate: {
      orgId: (value) => (value !== "" ? null : "Provide a valid username"),
      password: (value) => (isValidPassword(value) ? null : t("Login.passwordRuleWarning")),
    },
  });

  const handleApproval = (values: { orgId: string; password: string }) => {
    approveOrgRequest(
      {
        id: props.org._id,
        orgId: values.orgId,
        password: values.password,
      },
      (res) => {
        if (!selectedScopeName) {
          props.close();
          console.log(res);
          return;
        }

        assignScopeToMultipleOrgOrProduct(
          {
            orgIds: [values.orgId],
            scopeName: selectedScopeName,
          },
          () => {
            props.close();
            console.log(res);
          },
          (error) => {
            console.error("Organization approved but scope assignment failed", error);
            props.close();
          },
        );
      },
      (error) => {
        console.error(error);
      },
    );
  };

  return (
    <Modal opened={props.opened} onClose={props.close} title="Approve Organization Account Request">
      <form onSubmit={form.onSubmit(handleApproval)}>
        <Text>{"Name: " + props.org?.name}</Text>
        <Text>{"Address: " + props.org?.address}</Text>
        <Text>{"Email: " + props.org?.email}</Text>
        <Text>{t("accountType") + ": " + (props.org?.accountType?.join(", ") || "-")}</Text>
        <Text mb={"md"}>{t("accountInfo") + ": " + (props.org?.accountInfo || "-")}</Text>
        <Select
          label="Scope"
          data={scopeOptions}
          value={selectedScopeName ?? NO_SCOPE_OPTION_VALUE}
          onChange={(value) => setSelectedScopeName(value === NO_SCOPE_OPTION_VALUE ? null : value)}
          checkIconPosition="right"
          renderOption={({ option, checked }) => {
            const scopeOption = option as ScopeOption;
            const tooltipLabel =
              scopeOption.scopeItems && scopeOption.scopeItems.length > 0
                ? `Includes: ${scopeOption.scopeItems.join(", ")}`
                : undefined;

            return (
              <Tooltip
                label={tooltipLabel}
                disabled={!tooltipLabel}
                withArrow
                multiline
                maw={320}
                withinPortal
                position="right"
                zIndex={10000}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "0.75rem",
                  }}
                >
                  <span>{scopeOption.label}</span>
                  <span
                    style={{
                      display: "inline-flex",
                      width: "1rem",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {checked ? <IconCheck size={16} /> : null}
                  </span>
                </div>
              </Tooltip>
            );
          }}
          allowDeselect={false}
          disabled={isLoadingScopes}
          placeholder={isLoadingScopes ? "Loading scopes..." : "Select scope"}
          nothingFoundMessage="No scopes found"
          mb="md"
        />
        <TextInput
          withAsterisk
          label={t("Login.username")}
          key={form.key("orgId")}
          rightSection={
            <CloseButton
              aria-label="Clear input"
              onClick={() => form.setFieldValue("orgId", "")}
              style={{ display: form.getValues().orgId ? undefined : "none" }}
            />
          }
          {...form.getInputProps("orgId")}
        />
        <TextInput
          withAsterisk
          label={t("Login.password")}
          key={form.key("password")}
          rightSection={
            <CloseButton
              aria-label="Clear input"
              onClick={() =>
                form.getValues().password === ""
                  ? form.setFieldValue("password", generatePassword())
                  : form.setFieldValue("password", "")
              }
              icon={
                form.getValues().password === "" ? <IconRefresh size={18} /> : <IconX size={18} />
              }
            />
          }
          {...form.getInputProps("password")}
        />
        <Group justify="flex-end" mt="md">
          <Button type="submit">{t("submit")}</Button>
        </Group>
      </form>
    </Modal>
  );
}
